/**
 * Settings Manager for Cell Swap Game
 * 
 * Centralized settings management that handles persistence, 
 * synchronization with AudioController, and cross-scene access.
 */

import { AudioController, AudioSettings } from './AudioController'
import { LayoutMode } from '../utils/layout'

export type ControllerScheme = 'Swipe+Tap' | 'OneHanded' | 'Drag+Rotate' | 'Button Controls'
export type BackgroundChoice = 'pink' | 'blue' | 'rainbow' | 'purple'

/**
 * Get the Phaser texture key for a background setting
 * Normalizes legacy 'default' values to 'rainbow'
 */
export function getBackgroundTextureKey(background: BackgroundChoice | 'default'): string {
    // Normalize legacy 'default' to 'rainbow'
    if (background === 'default') {
        return 'bg-rainbow'
    }
    return `bg-${background}`
}

export interface GameSettings {
    // Audio settings
    musicEnabled: boolean
    musicVolume: number // 0.0 to 1.0
    soundEnabled: boolean
    masterVolume: number

    // Game settings
    difficulty: 'Easy' | 'Normal' | 'Hard'

    // Puzzle Mode settings
    puzzleModeLevel: number // 0-20 (starting level)
    puzzleModeSpeed: 'Low' | 'Med' | 'Hi'

    // Display settings
    displayMode: 'Windowed' | 'Fullscreen'

    // Layout settings
    layoutMode: LayoutMode
    debugOverlayEnabled: boolean

    // Visual settings
    cellJiggleEnabled: boolean
    backgroundImage: BackgroundChoice
    gridVisible: boolean

    // Mobile settings
    touchControlsEnabled: boolean
    hapticFeedbackEnabled: boolean
    touchSensitivity: number
    controllerScheme: ControllerScheme

    // Internal version for settings migration
    version: number
}

export class SettingsManager {
    private static instance: SettingsManager | null = null
    private settings: GameSettings
    private audioController: AudioController | null = null
    private readonly STORAGE_KEY = 'cell-swap-settings'
    private readonly CURRENT_VERSION = 2

    private constructor() {
        this.settings = this.getDefaultSettings()
        this.loadSettings()
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): SettingsManager {
        if (!SettingsManager.instance) {
            SettingsManager.instance = new SettingsManager()
        }
        return SettingsManager.instance
    }

    /**
     * Register AudioController for real-time audio control
     */
    public setAudioController(audioController: AudioController): void {
        this.audioController = audioController
        this.applyAudioSettings()
    }

    /**
     * Get the registered AudioController instance
     */
    public getAudioController(): AudioController | null {
        return this.audioController
    }

    /**
     * Get default settings
     * Desktop users get Button Controls by default (keyboard + on-screen buttons)
     * Mobile users get Swipe+Tap by default (touch gestures)
     */
    private getDefaultSettings(): GameSettings {
        // Detect if device has touch capability
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

        // Default to Button Controls for desktop, Swipe+Tap for mobile
        const defaultControllerScheme: ControllerScheme = isTouchDevice ? 'Swipe+Tap' : 'Button Controls'

        // Only enable haptic feedback on mobile devices (desktop doesn't support vibration)
        const defaultHapticEnabled = isTouchDevice

        return {
            musicEnabled: true,
            musicVolume: 0.2, // Default music volume at 20%
            soundEnabled: true,
            masterVolume: 0.7,
            difficulty: 'Easy',
            puzzleModeLevel: 0, // Default to level 0 (4 viruses)
            puzzleModeSpeed: 'Med', // Default to medium speed
            displayMode: 'Fullscreen',
            layoutMode: 'portraitTopBottomHUD', // Auto-detects portrait/landscape
            debugOverlayEnabled: false, // Debug overlay off by default
            cellJiggleEnabled: true, // Default: cells jiggle/animate
            backgroundImage: 'rainbow', // Default background
            gridVisible: true, // Default: grid is visible
            touchControlsEnabled: true,
            hapticFeedbackEnabled: defaultHapticEnabled,
            touchSensitivity: 1.0,
            controllerScheme: defaultControllerScheme,
            version: this.CURRENT_VERSION
        }
    }

    /**
     * Load settings from localStorage
     */
    private loadSettings(): void {
        try {
            const savedSettings = localStorage.getItem(this.STORAGE_KEY)
            if (savedSettings) {
                const parsed = JSON.parse(savedSettings)

                // Handle settings migration if needed
                if (parsed.version !== this.CURRENT_VERSION) {
                    this.settings = this.migrateSettings(parsed)
                } else {
                    this.settings = { ...this.getDefaultSettings(), ...parsed }
                    // Ensure musicVolume exists (for older saves with same version)
                    if (typeof this.settings.musicVolume !== 'number') {
                        this.settings.musicVolume = this.settings.musicEnabled ? 0.2 : 0
                    }
                    // Ensure gridVisible exists (for older saves with same version)
                    if (typeof this.settings.gridVisible !== 'boolean') {
                        this.settings.gridVisible = true
                    }
                }
            }
        } catch (error) {
            console.warn('SettingsManager: Failed to load settings, using defaults:', error)
            this.settings = this.getDefaultSettings()
        }
    }

    /**
     * Migrate settings from older versions
     */
    private migrateSettings(oldSettings: any): GameSettings {
        const newSettings = this.getDefaultSettings()

        // Migrate known properties
        if (typeof oldSettings.musicEnabled === 'boolean') {
            newSettings.musicEnabled = oldSettings.musicEnabled
            // Migrate musicEnabled to musicVolume
            if (!oldSettings.musicVolume) {
                newSettings.musicVolume = oldSettings.musicEnabled ? 0.2 : 0
            }
        }
        if (typeof oldSettings.musicVolume === 'number') {
            newSettings.musicVolume = oldSettings.musicVolume
        }
        if (typeof oldSettings.soundEnabled === 'boolean') {
            newSettings.soundEnabled = oldSettings.soundEnabled
        }
        if (typeof oldSettings.difficulty === 'string') {
            newSettings.difficulty = oldSettings.difficulty
        }
        if (typeof oldSettings.displayMode === 'string') {
            newSettings.displayMode = oldSettings.displayMode
        }
        if (typeof oldSettings.touchControlsEnabled === 'boolean') {
            newSettings.touchControlsEnabled = oldSettings.touchControlsEnabled
        }
        if (typeof oldSettings.hapticFeedbackEnabled === 'boolean') {
            newSettings.hapticFeedbackEnabled = oldSettings.hapticFeedbackEnabled
        }
        if (typeof oldSettings.touchSensitivity === 'number') {
            newSettings.touchSensitivity = oldSettings.touchSensitivity
        }
        if (typeof oldSettings.controllerScheme === 'string') {
            newSettings.controllerScheme = oldSettings.controllerScheme as ControllerScheme
        }
        if (typeof oldSettings.puzzleModeLevel === 'number') {
            newSettings.puzzleModeLevel = Math.max(0, Math.min(20, oldSettings.puzzleModeLevel))
        }
        if (typeof oldSettings.puzzleModeSpeed === 'string' &&
            ['Low', 'Med', 'Hi'].includes(oldSettings.puzzleModeSpeed)) {
            newSettings.puzzleModeSpeed = oldSettings.puzzleModeSpeed
        }
        if (typeof oldSettings.cellJiggleEnabled === 'boolean') {
            newSettings.cellJiggleEnabled = oldSettings.cellJiggleEnabled
        }
        if (typeof oldSettings.backgroundImage === 'string') {
            // Migrate legacy 'default' to 'rainbow'
            if (oldSettings.backgroundImage === 'default') {
                newSettings.backgroundImage = 'rainbow'
            } else if (['pink', 'blue', 'rainbow', 'purple'].includes(oldSettings.backgroundImage)) {
                newSettings.backgroundImage = oldSettings.backgroundImage
            }
        }
        if (typeof oldSettings.gridVisible === 'boolean') {
            newSettings.gridVisible = oldSettings.gridVisible
        }

        console.log('SettingsManager: Migrated settings from version', oldSettings.version || 0, 'to', this.CURRENT_VERSION)
        return newSettings
    }

    /**
     * Save settings to localStorage
     */
    private saveSettings(): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.settings))
        } catch (error) {
            console.warn('SettingsManager: Failed to save settings:', error)
        }
    }

    /**
     * Apply audio settings to AudioController
     */
    private applyAudioSettings(): void {
        if (!this.audioController) return

        const audioSettings: Partial<AudioSettings> = {
            masterVolume: this.settings.masterVolume,
            musicVolume: this.settings.musicVolume, // Use direct volume (0.0 means muted)
            sfxVolume: this.settings.soundEnabled ? 0.8 : 0,
            muted: this.settings.musicVolume === 0 && !this.settings.soundEnabled
        }

        this.audioController.updateSettings(audioSettings)
    }

    /**
     * Get current settings
     */
    public getSettings(): GameSettings {
        return { ...this.settings }
    }

    /**
     * Update music enabled setting
     */
    public setMusicEnabled(enabled: boolean): void {
        this.settings.musicEnabled = enabled
        this.saveSettings()
        this.applyAudioSettings()
    }

    /**
     * Update music volume (0.0 to 1.0)
     */
    public setMusicVolume(volume: number): void {
        this.settings.musicVolume = Math.max(0, Math.min(1, volume))
        this.saveSettings()
        this.applyAudioSettings()
    }

    /**
     * Update sound effects enabled setting
     */
    public setSoundEnabled(enabled: boolean): void {
        this.settings.soundEnabled = enabled
        this.saveSettings()
        this.applyAudioSettings()
    }

    /**
     * Update master volume
     */
    public setMasterVolume(volume: number): void {
        this.settings.masterVolume = Math.max(0, Math.min(1, volume))
        this.saveSettings()
        this.applyAudioSettings()
    }

    /**
     * Update difficulty setting
     */
    public setDifficulty(difficulty: 'Easy' | 'Normal' | 'Hard'): void {
        this.settings.difficulty = difficulty
        this.saveSettings()
    }

    /**
     * Update display mode setting
     */
    public setDisplayMode(mode: 'Windowed' | 'Fullscreen'): void {
        this.settings.displayMode = mode
        this.saveSettings()
    }

    /**
     * Update cell jiggle enabled setting
     */
    public setCellJiggleEnabled(enabled: boolean): void {
        this.settings.cellJiggleEnabled = enabled
        this.saveSettings()
    }

    /**
     * Check if cell jiggle is enabled
     */
    public isCellJiggleEnabled(): boolean {
        return this.settings.cellJiggleEnabled
    }

    /**
     * Update background image setting
     */
    public setBackgroundImage(background: 'default' | 'pink' | 'blue' | 'rainbow' | 'purple'): void {
        this.settings.backgroundImage = background
        this.saveSettings()
    }

    /**
     * Get current background image
     */
    public getBackgroundImage(): 'default' | 'pink' | 'blue' | 'rainbow' | 'purple' {
        return this.settings.backgroundImage
    }

    /**
     * Reset to default settings
     */
    public resetToDefaults(): void {
        this.settings = this.getDefaultSettings()
        this.saveSettings()
        this.applyAudioSettings()
    }

    /**
     * Get audio-specific settings for AudioController
     */
    public getAudioSettings(): Partial<AudioSettings> {
        return {
            masterVolume: this.settings.masterVolume,
            musicVolume: this.settings.musicVolume, // Use direct volume
            sfxVolume: this.settings.soundEnabled ? 0.8 : 0,
            muted: this.settings.musicVolume === 0 && !this.settings.soundEnabled
        }
    }

    /**
     * Check if music is enabled
     */
    public isMusicEnabled(): boolean {
        return this.settings.musicEnabled
    }

    /**
     * Check if sound effects are enabled
     */
    public isSoundEnabled(): boolean {
        return this.settings.soundEnabled
    }

    /**
     * Get current difficulty
     */
    public getDifficulty(): string {
        return this.settings.difficulty
    }

    /**
     * Get current display mode
     */
    public getDisplayMode(): string {
        return this.settings.displayMode
    }

    /**
     * Initialize settings for a scene
     * Call this when creating any scene that needs settings
     */
    public initializeForScene(audioController?: AudioController): GameSettings {
        if (audioController) {
            this.setAudioController(audioController)
        }
        return this.getSettings()
    }

    /**
     * Update touch controls enabled setting
     */
    public setTouchControlsEnabled(enabled: boolean): void {
        this.settings.touchControlsEnabled = enabled
        this.saveSettings()
    }

    /**
     * Update haptic feedback enabled setting
     */
    public setHapticFeedbackEnabled(enabled: boolean): void {
        this.settings.hapticFeedbackEnabled = enabled
        this.saveSettings()
    }

    /**
     * Update touch sensitivity setting
     */
    public setTouchSensitivity(sensitivity: number): void {
        this.settings.touchSensitivity = Math.max(0.5, Math.min(2.0, sensitivity))
        this.saveSettings()
    }

    /**
     * Check if touch controls are enabled
     */
    public isTouchControlsEnabled(): boolean {
        return this.settings.touchControlsEnabled
    }

    /**
     * Check if haptic feedback is enabled
     */
    public isHapticFeedbackEnabled(): boolean {
        return this.settings.hapticFeedbackEnabled
    }

    /**
     * Get touch sensitivity
     */
    public getTouchSensitivity(): number {
        return this.settings.touchSensitivity
    }

    /**
     * Update controller scheme setting
     */
    public setControllerScheme(scheme: ControllerScheme): void {
        this.settings.controllerScheme = scheme
        this.saveSettings()
    }

    /**
     * Get controller scheme
     */
    public getControllerScheme(): ControllerScheme {
        return this.settings.controllerScheme
    }

    /**
     * Update puzzle mode level setting
     */
    public setPuzzleModeLevel(level: number): void {
        this.settings.puzzleModeLevel = Math.max(0, Math.min(20, level))
        this.saveSettings()
    }

    /**
     * Get puzzle mode level
     */
    public getPuzzleModeLevel(): number {
        return this.settings.puzzleModeLevel
    }

    /**
     * Update puzzle mode speed setting
     */
    public setPuzzleModeSpeed(speed: 'Low' | 'Med' | 'Hi'): void {
        this.settings.puzzleModeSpeed = speed
        this.saveSettings()
    }

    /**
     * Get puzzle mode speed
     */
    public getPuzzleModeSpeed(): 'Low' | 'Med' | 'Hi' {
        return this.settings.puzzleModeSpeed
    }

    /**
     * Update layout mode setting
     */
    public setLayoutMode(mode: LayoutMode): void {
        this.settings.layoutMode = mode
        this.saveSettings()
    }

    /**
     * Get layout mode - returns stored mode without auto-detection
     * For auto-detection, use getLayoutModeWithAutoDetect()
     */
    public getLayoutMode(): LayoutMode {
        return this.settings.layoutMode
    }

    /**
     * Get layout mode with automatic portrait detection
     * Returns portraitTopBottomHUD automatically if in portrait orientation
     * and the current mode supports auto-detection
     */
    public getLayoutModeWithAutoDetect(): LayoutMode {
        // Check if we should auto-detect portrait mode
        if (this.shouldUsePortraitMode()) {
            return 'portraitTopBottomHUD'
        }
        return this.settings.layoutMode
    }

    /**
     * Check if portrait mode should be used based on screen orientation
     */
    private shouldUsePortraitMode(): boolean {
        if (typeof window === 'undefined') return false

        const width = window.innerWidth
        const height = window.innerHeight
        const aspectRatio = width / height

        // Portrait orientation: aspect ratio < 1
        // Auto-enable portrait mode if screen is portrait-oriented
        return aspectRatio < 1
    }

    /**
     * Enable or disable automatic portrait mode detection
     */
    public setPortraitModeEnabled(enabled: boolean): void {
        if (enabled) {
            // Enable portrait mode detection (use portraitTopBottomHUD as base)
            this.settings.layoutMode = 'portraitTopBottomHUD'
        } else {
            // Disable portrait mode (fallback to classic)
            this.settings.layoutMode = 'classicSidebars'
        }
        this.saveSettings()
    }

    /**
     * Update debug overlay enabled setting
     */
    public setDebugOverlayEnabled(enabled: boolean): void {
        this.settings.debugOverlayEnabled = enabled
        this.saveSettings()
    }

    /**
     * Check if debug overlay is enabled
     */
    public isDebugOverlayEnabled(): boolean {
        return this.settings.debugOverlayEnabled
    }

    /**
     * Update grid visibility setting
     */
    public setGridVisible(visible: boolean): void {
        this.settings.gridVisible = visible
        this.saveSettings()
    }

    /**
     * Check if grid is visible
     */
    public isGridVisible(): boolean {
        return this.settings.gridVisible
    }
}